import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  CalendarIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  ClockIcon,
  BoltIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  LinkIcon,
  ArrowPathIcon,
  CodeBracketIcon,
  BookmarkIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';
import { extractApiError } from '../../utils/apiError';
import { api } from '../../services/api';
import {
  createCampaign,
  updateCampaign,
  deleteCampaign,
  updateCampaignStatus,
  uploadContactsFile,
  uploadContactsJson,
  validateContactsFile,
  validateContactsJson,
  listPhoneNumbers,
  fetchWebhookContacts,
  updateFieldDescriptions,
  listFieldTemplates,
  createFieldTemplate,
  deleteFieldTemplate,
  getCampaign,
} from '../../services/campaignApi';

// Wizard steps configuration - uses translation keys resolved in Stepper
const stepKeys = [
  { id: 1, nameKey: 'campaignWizard.step1', descKey: 'campaignWizard.step1' },
  { id: 2, nameKey: 'campaignWizard.step2', descKey: 'campaignWizard.uploadContacts' },
  { id: 3, nameKey: 'campaignWizard.step3', descKey: 'campaignWizard.step3' },
  { id: 4, nameKey: 'campaignWizard.step4', descKey: 'campaignWizard.reviewLaunch' },
];

const CALLING_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${hour.toString().padStart(2, '0')}:00`,
}));

// Stepper component
const Stepper = ({ currentStep, t }) => (
  <nav className="mb-8">
    <ol className="flex items-center justify-center">
      {stepKeys.map((step, index) => (
        <li key={step.id} className="flex items-center">
          <div className="flex items-center">
            <span
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium transition-colors',
                currentStep > step.id
                  ? 'bg-primary border-primary text-white'
                  : currentStep === step.id
                  ? 'border-primary text-primary bg-primary-50'
                  : 'border-border text-muted'
              )}
            >
              {currentStep > step.id ? (
                <CheckIcon className="w-4 h-4" />
              ) : (
                step.id
              )}
            </span>
            <div className="ml-3 hidden sm:block">
              <p className={cn(
                'text-sm font-medium',
                currentStep >= step.id ? 'text-foreground' : 'text-muted'
              )}>
                {t(step.nameKey)}
              </p>
              <p className="text-xs text-muted">{t(step.descKey)}</p>
            </div>
          </div>
          {index < stepKeys.length - 1 && (
            <div
              className={cn(
                'w-12 sm:w-24 h-0.5 mx-4',
                currentStep > step.id ? 'bg-primary' : 'bg-secondary'
              )}
            />
          )}
        </li>
      ))}
    </ol>
  </nav>
);

// Step 1: Basics
const StepBasics = ({ data, onChange, agents, phoneNumbers, t }) => (
  <div className="space-y-6 max-w-xl mx-auto">
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.campaignName')} *
      </label>
      <input
        type="text"
        value={data.name}
        onChange={(e) => onChange('name', e.target.value)}
        placeholder={t('campaignWizard.campaignNamePlaceholder')}
        className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.campaignDescription')}
      </label>
      <textarea
        value={data.description}
        onChange={(e) => onChange('description', e.target.value)}
        placeholder={t('campaignWizard.campaignDescPlaceholder')}
        rows={3}
        className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-card"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.campaignGoal', 'Campaign Goal')}
      </label>
      <textarea
        value={data.goalDescription}
        onChange={(e) => onChange('goalDescription', e.target.value)}
        placeholder="e.g., Recover overdue balances by securing a payment promise or next contact step"
        rows={2}
        maxLength={500}
        className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-card"
      />
      <p className="text-xs text-muted mt-1">
        {t('campaignWizard.campaignGoalHelp', 'The AI agent will focus its call report on this goal')}
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.selectAgent')} *
      </label>
      <select
        value={data.agentId}
        onChange={(e) => onChange('agentId', e.target.value)}
        className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-card"
      >
        <option value="">{t('calls.chooseAgent', 'Choose an agent...')}</option>
        {agents.filter(Boolean).map((agent) => (
          <option key={agent.id} value={agent.id}>
            {agent?.name} ({agent?.language}){agent?.is_shared ? ` — ${t('agents.shared')}` : ''}
          </option>
        ))}
      </select>
    </div>

    {data.agentId && (
      <div className="p-4 bg-background rounded-xl border border-border">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
            <UserGroupIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">
              {agents.find(a => a.id === data.agentId)?.name}
            </p>
            <p className="text-sm text-muted">
              {t('campaignWizard.agentVoice', { voice: agents.find(a => a.id === data.agentId)?.voice, lang: agents.find(a => a.id === data.agentId)?.language })}
            </p>
          </div>
        </div>
      </div>
    )}

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.callerIdLabel')} *
        <span className="text-xs text-muted ml-2">({data.callerIds.length} {t('common.selected', 'selected')})</span>
      </label>
      {/* Selected numbers as chips */}
      {data.callerIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {data.callerIds.map((num) => {
            const pn = phoneNumbers.find(p => p?.number === num);
            return (
              <span key={num} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary text-sm font-medium">
                {num} {pn ? `(${pn.label})` : ''}
                <button
                  type="button"
                  onClick={() => onChange('callerIds', data.callerIds.filter(id => id !== num))}
                  className="hover:text-red-500 transition-colors"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {/* Dropdown to add more numbers */}
      <select
        value=""
        onChange={(e) => {
          if (e.target.value && !data.callerIds.includes(e.target.value)) {
            onChange('callerIds', [...data.callerIds, e.target.value]);
          }
        }}
        className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-card"
      >
        <option value="">{t('campaignWizard.addPhoneNumber', 'Add a phone number...')}</option>
        {phoneNumbers
          .filter(Boolean)
          .filter(pn => !data.callerIds.includes(pn.number))
          .map((pn, idx) => (
            <option key={idx} value={pn.number}>
              {pn?.number} ({pn?.label})
            </option>
          ))}
      </select>
    </div>

    {/* Gender Switch toggle */}
    {data.agentId && (() => {
      const selectedAgent = agents.find(a => a.id === data.agentId);
      const hasSecondary = selectedAgent?.elevenlabs_voice_id_secondary || selectedAgent?.voice_secondary;
      return (
        <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
          <div>
            <label className="text-sm font-medium text-foreground">
              {t('campaignWizard.genderSwitch')}
            </label>
            <p className="text-xs text-muted mt-1">
              {hasSecondary
                ? t('campaignWizard.genderSwitchHelp')
                : t('campaignWizard.genderSwitchRequiresSecondary')}
            </p>
          </div>
          <button
            type="button"
            disabled={!hasSecondary}
            onClick={() => onChange('genderSwitch', !data.genderSwitch)}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0',
              !hasSecondary
                ? 'bg-secondary cursor-not-allowed opacity-50'
                : data.genderSwitch
                ? 'bg-primary'
                : 'bg-secondary'
            )}
          >
            <span className={cn(
              'toggle-knob inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              data.genderSwitch && hasSecondary ? 'translate-x-6' : 'translate-x-1'
            )} />
          </button>
        </div>
      );
    })()}

    {/* Visibility */}
    <div className="p-4 bg-background rounded-xl border border-border">
      <label className="text-sm font-medium text-foreground block mb-2">{t('campaignWizard.visibility', 'Campaign Visibility')}</label>
      <div className="flex gap-4">
        {['individual', 'organization'].map((v) => (
          <label key={v} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="visibility"
              value={v}
              checked={data.visibility === v}
              onChange={() => onChange('visibility', v)}
              className="accent-primary"
            />
            <span className="text-sm text-foreground">
              {v === 'individual' ? t('campaignWizard.visibility_individual', 'Individual') : t('campaignWizard.visibility_organization', 'Organization')}
            </span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted mt-1">{t('campaignWizard.visibilityHelp', 'Individual: only you and admins receive this campaign\'s notifications. Organization: everyone in the organization receives them.')}</p>
    </div>
  </div>
);

// Step 2: Audience
const StepAudience = ({ data, onChange, campaignId, onEnsureCampaignId, fieldTemplates, setFieldTemplates }) => {
  const { t } = useTranslation();
  const [inputMethod, setInputMethod] = useState('upload');
  const [isValidating, setIsValidating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [invalidDetails, setInvalidDetails] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [webhookPresets, setWebhookPresets] = useState([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const templateDropdownRef = useRef(null);

  // Fetch webhook presets on mount
  useEffect(() => {
    api.get('/webhook-presets').then(res => setWebhookPresets(res.data || [])).catch(() => {});
  }, []);

  // Close template dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    setSelectedFile(file);
    setUploadError(null);
    setIsValidating(true);

    try {
      const res = await validateContactsFile(file, 'MX', campaignId || null);
      const result = res.data;
      onChange('validCount', result.valid_count);
      onChange('invalidCount', result.invalid_count);
      onChange('duplicateCount', result.duplicate_count);
      onChange('sharedPhoneCount', result.shared_phone_count || 0);
      onChange('sharedNumberCount', result.shared_number_count || 0);
      // Hilda import-names guard (aug27): surface nameless (empty-name) count so
      // the preview can warn instead of silently importing phone-only contacts.
      onChange('namelessCount', result.nameless_count || 0);
      onChange('totalContacts', result.valid_count);
      onChange('uploadedFile', file);
      setPreviewContacts(result.valid_preview || []);
      setInvalidDetails(result.invalid_details || []);
      if (result.valid_count === 0) {
        // Defensive: surface parse failure explicitly so the user understands
        // why the Next button is still disabled.
        const reason = result.invalid_details?.[0]?.reason || 'no_contacts_parsed';
        setUploadError(
          `File parsed but 0 valid contacts found. Check column headers or file format. (${reason})`
        );
      } else {
        toast.success(t('success.validContactsFound', { count: result.valid_count }));
      }
    } catch (err) {
      const detail = extractApiError(err, t('errors.failedValidateFile'));
      setUploadError(detail);
      toast.error(detail);
    } finally {
      setIsValidating(false);
    }
  }, [onChange, campaignId]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handlePasteValidate = useCallback(async () => {
    if (!data.phoneList?.trim()) return;
    setIsValidating(true);
    try {
      const lines = data.phoneList.split('\n').filter(l => l.trim());
      const contacts = lines.map(phone => ({ phone: phone.trim() }));
      const res = await validateContactsJson(contacts);
      const result = res.data;
      onChange('validCount', result.valid_count);
      onChange('invalidCount', result.invalid_count);
      onChange('duplicateCount', result.duplicate_count);
      onChange('sharedPhoneCount', result.shared_phone_count || 0);
      onChange('sharedNumberCount', result.shared_number_count || 0);
      // Hilda import-names guard (aug27): nameless count for phone-only paste.
      onChange('namelessCount', result.nameless_count || 0);
      onChange('totalContacts', result.valid_count);
      onChange('parsedContacts', contacts);
      setPreviewContacts(result.valid_preview || []);
      setInvalidDetails(result.invalid_details || []);
    } catch (err) {
      toast.error(extractApiError(err, t('errors.failedValidateNumbers')));
    } finally {
      setIsValidating(false);
    }
  }, [data.phoneList, onChange]);

  // Fetch contacts from Struct Datafy webhook URL
  // NOTE: campaignId may be null for new campaigns — caller must ensure it is
  // created before invoking this (see onEnsureCampaignId prop).
  const handleFetchWebhook = useCallback(async () => {
    if (!data.webhookUrl?.trim()) return;
    // Ensure a campaign draft exists for the webhook call (needed by backend)
    const cid = campaignId || (await onEnsureCampaignId());
    if (!cid) return; // creation failed — error already toasted by caller
    setWebhookLoading(true);
    try {
      const res = await fetchWebhookContacts(cid, data.webhookUrl.trim());
      const result = res.data;
      onChange('validCount', result.valid_count);
      onChange('invalidCount', result.invalid_count);
      onChange('duplicateCount', result.duplicate_count);
      onChange('sharedPhoneCount', result.shared_phone_count || 0);
      onChange('sharedNumberCount', result.shared_number_count || 0);
      // Hilda import-names guard (aug27): nameless count for webhook fetch.
      onChange('namelessCount', result.nameless_count || 0);
      onChange('totalContacts', result.total_contacts);
      onChange('webhookContacts', result.total_contacts);
      setPreviewContacts(result.valid_preview || []);
      setInvalidDetails(result.invalid_details || []);
      if (result.valid_count > 0) {
        toast.success(t('success.validContactsFetched', { count: result.valid_count }));
      }
    } catch (err) {
      toast.error(extractApiError(err, t('errors.failedFetchWebhookContacts')));
    } finally {
      setWebhookLoading(false);
    }
  }, [campaignId, data.webhookUrl, onChange]);

  // Validate pasted Datafy JSON
  const handleDatafyValidate = useCallback(async () => {
    if (!data.datafyJson?.trim()) return;
    setIsValidating(true);
    try {
      const parsed = JSON.parse(data.datafyJson);
      const contacts = parsed.rows || parsed.contacts || (Array.isArray(parsed) ? parsed : [parsed]);
      const res = await validateContactsJson(contacts);
      const result = res.data;
      onChange('validCount', result.valid_count);
      onChange('invalidCount', result.invalid_count);
      onChange('duplicateCount', result.duplicate_count);
      onChange('sharedPhoneCount', result.shared_phone_count || 0);
      onChange('sharedNumberCount', result.shared_number_count || 0);
      // Hilda import-names guard (aug27): nameless count for Datafy JSON paste.
      onChange('namelessCount', result.nameless_count || 0);
      onChange('totalContacts', result.valid_count);
      onChange('parsedContacts', contacts);
      setPreviewContacts(result.valid_preview || []);
      setInvalidDetails(result.invalid_details || []);
      if (result.valid_count > 0) {
        toast.success(t('success.validContactsFound', { count: result.valid_count }));
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error(t('errors.invalidJsonFormat'));
      } else {
        toast.error(extractApiError(err, t('errors.failedValidate')));
      }
    } finally {
      setIsValidating(false);
    }
  }, [data.datafyJson, onChange]);

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      {/* Input method tabs */}
      <div className="flex gap-2 p-1 bg-secondary rounded-xl">
        <button
          onClick={() => setInputMethod('upload')}
          className={cn(
            'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors',
            inputMethod === 'upload'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted hover:text-foreground'
          )}
        >
          <CloudArrowUpIcon className="w-4 h-4 inline mr-1" />
          {t('campaignWizard.uploadFile')}
        </button>
        <button
          onClick={() => setInputMethod('paste')}
          className={cn(
            'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors',
            inputMethod === 'paste'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted hover:text-foreground'
          )}
        >
          <DocumentTextIcon className="w-4 h-4 inline mr-1" />
          {t('campaignWizard.pasteList')}
        </button>
        <button
          onClick={() => setInputMethod('integration')}
          className={cn(
            'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors',
            inputMethod === 'integration'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted hover:text-foreground'
          )}
        >
          <LinkIcon className="w-4 h-4 inline mr-1" />
          {t('campaignWizard.integration')}
        </button>
      </div>

      {inputMethod === 'upload' && (
        <div>
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <DocumentTextIcon className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                  <p className="text-xs text-muted">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    onChange('uploadedFile', null);
                    onChange('validCount', 0);
                    onChange('invalidCount', 0);
                    onChange('duplicateCount', 0);
                    onChange('sharedPhoneCount', 0);
                    onChange('sharedNumberCount', 0);
                    onChange('totalContacts', 0);
                    setPreviewContacts([]);
                    setInvalidDetails([]);
                  }}
                  className="p-1 hover:bg-hover rounded"
                >
                  <XMarkIcon className="w-4 h-4 text-muted" />
                </button>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--muted)' }} />
                <p className="text-sm font-medium text-foreground mb-1">
                  {t('campaignWizard.dragAndDrop')}
                </p>
                <p className="text-xs text-muted">{t('campaignWizard.fileTypes')}</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
          <p className="text-xs text-muted mt-2">
            Columns: phone/telefono, name/nombre (optional), external_id (optional). Extra columns become context.
          </p>
        </div>
      )}

      {inputMethod === 'paste' && (
        <div>
          <textarea
            value={data.phoneList}
            onChange={(e) => onChange('phoneList', e.target.value)}
            placeholder={t('campaignWizard.enterPhoneNumbers')}
            rows={8}
            className="w-full px-4 py-3 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-card"
          />
          <button
            onClick={handlePasteValidate}
            disabled={!data.phoneList?.trim() || isValidating}
            className={cn(
              'mt-2 w-full py-2 rounded-full text-sm font-medium transition-colors',
              data.phoneList?.trim() && !isValidating
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-secondary text-muted cursor-not-allowed'
            )}
          >
            {isValidating ? t('campaignWizard.validatingContacts') : t('campaignWizard.validateNumbers')}
          </button>
        </div>
      )}

      {inputMethod === 'integration' && (
        <div className="space-y-6">
          {/* Fetch from Struct Datafy URL */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Struct Datafy Webhook
            </h4>
            <p className="text-xs text-muted mb-3">
              Pega la URL del webhook de Struct Datafy para importar los contactos automáticamente.
            </p>
            {data.webhookContacts > 0 ? (
              /* Locked/saved state after successful fetch */
              <div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={data.webhookUrl || ''}
                    className="flex-1 px-3 py-2.5 bg-secondary border border-green-300 rounded-lg text-sm font-mono text-muted cursor-default"
                  />
                  <button
                    onClick={() => {
                      onChange('webhookContacts', 0);
                      onChange('validCount', 0);
                      onChange('invalidCount', 0);
                      onChange('duplicateCount', 0);
                      onChange('sharedPhoneCount', 0);
                      onChange('sharedNumberCount', 0);
                      onChange('totalContacts', 0);
                    }}
                    className="px-3 py-2.5 border border-border rounded-lg text-sm text-muted hover:text-foreground hover:bg-hover transition-colors"
                    title="Cambiar URL"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm font-medium text-green-700">
                    {t('campaignWizard.numberCount', { count: data.webhookContacts })} {t('campaignWizard.integration').toLowerCase()}
                  </p>
                </div>
              </div>
            ) : (
              /* Editable state — preset dropdown + paste URL and fetch */
              <div className="space-y-2">
                {webhookPresets.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const preset = webhookPresets.find(p => p.id === e.target.value);
                      if (preset) {
                        onChange('webhookUrl', preset.url);
                      }
                    }}
                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">{t('campaignWizard.selectWebhookPreset')}</option>
                    {webhookPresets.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                <div className="flex gap-2">
                  <textarea
                    rows={2}
                    value={data.webhookUrl || ''}
                    onChange={(e) => onChange('webhookUrl', e.target.value)}
                    placeholder="https://structdatafy.example.com/api/webhook/..."
                    className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary bg-card resize-y overflow-y-auto"
                  />
                  <button
                    onClick={handleFetchWebhook}
                    disabled={!data.webhookUrl?.trim() || webhookLoading}
                    className={cn(
                      'px-4 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap',
                      data.webhookUrl?.trim() && campaignId && !webhookLoading
                        ? 'bg-primary text-white hover:bg-primary-hover'
                        : 'bg-secondary text-muted cursor-not-allowed'
                    )}
                  >
                    {webhookLoading ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      t('campaignWizard.fetchContacts')
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-card text-muted">{t('campaignWizard.orPasteJson')}</span>
            </div>
          </div>

          {/* Paste Datafy JSON Section */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <CodeBracketIcon className="w-4 h-4" />
              Pegar JSON
            </h4>
            <textarea
              value={data.datafyJson || ''}
              onChange={(e) => onChange('datafyJson', e.target.value)}
              placeholder={'Pega el output de Struct Datafy:\n{"rows": [{"telefono": "+52...", "nombre": "..."}]}'}
              rows={6}
              className="w-full px-4 py-3 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-card"
            />
            <button
              onClick={handleDatafyValidate}
              disabled={!data.datafyJson?.trim() || isValidating}
              className={cn(
                'mt-2 w-full py-2 rounded-full text-sm font-medium transition-colors',
                data.datafyJson?.trim() && !isValidating
                  ? 'bg-primary text-white hover:bg-primary-hover'
                  : 'bg-secondary text-muted cursor-not-allowed'
              )}
            >
              {isValidating ? t('campaignWizard.validatingContacts') : t('campaignWizard.validateNumbers')}
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {/* Upload error banner */}
      {uploadError && !isValidating && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <span className="mt-0.5">⚠️</span>
          <span>{uploadError}</span>
        </div>
      )}

      {isValidating && (
        <div className="text-center py-4">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm text-muted">{t('campaignWizard.validatingContacts')}</p>
        </div>
      )}

      {/* Preview stats */}
      {(data.validCount > 0 || data.invalidCount > 0) && !isValidating && (
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-2xl font-semibold text-green-700">{data.validCount || 0}</p>
            <p className="text-xs text-green-600">{t('calls.validNumbers')}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-2xl font-semibold text-red-700">{data.invalidCount || 0}</p>
            <p className="text-xs text-red-600">{t('calls.invalidNumbers')}</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            {/*
              Truthful "Duplicados" badge.
              - shared_phone_count = kept accounts that share their phone with
                another kept account (accounts are NOT dropped). This is what the
                user actually sees when e.g. 3 accounts legitimately share one
                phone (Gran Chapur cobranza): badge shows 3, not 0.
              - Fall back to duplicateCount (collapsed/dropped records) when there
                is no shared-phone data (e.g. non-account files).
            */}
            <p className="text-2xl font-semibold text-yellow-700">
              {(data.sharedPhoneCount || 0) > 0 ? data.sharedPhoneCount : (data.duplicateCount || 0)}
            </p>
            <p className="text-xs text-yellow-600">
              {(data.sharedPhoneCount || 0) > 0
                ? t('calls.sharedPhones', {
                    accounts: data.sharedPhoneCount,
                    numbers: data.sharedNumberCount || 1,
                    defaultValue: '{{accounts}} cuentas comparten {{numbers}} número(s)',
                  })
                : t('calls.duplicates')}
            </p>
          </div>
        </div>
      )}

      {/*
        Hilda import-names guard (aug27): if contacts are about to be imported
        with empty names, surface it here instead of silently proceeding.
        Hilda's campaign 91154284 went out with 120/120 nameless contacts (a
        phone-only upload_json body) and the agent then greeted without a name.
        We do NOT block the import (phone-only campaigns are valid) -- this is a
        non-blocking warning so the user can go back and map/add a name column.
      */}
      {(data.namelessCount || 0) > 0 && !isValidating && (
        <div
          data-testid="nameless-warning"
          className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800"
        >
          <span className="mt-0.5">⚠️</span>
          <span>
            {t('campaignWizard.namelessWarning', {
              count: data.namelessCount,
              total: data.validCount || 0,
              defaultValue:
                '{{count}} de {{total}} contactos se importarán sin nombre. El agente los saludará sin nombre. Si tu archivo tiene una columna de nombre, regresa y mapéala; si es una campaña solo-teléfono, puedes continuar.',
            })}
          </span>
        </div>
      )}

      {/* Preview table */}
      {previewContacts.length > 0 && !isValidating && (
        <div>
          <p className="text-sm font-medium text-foreground mb-2">{t('campaignWizard.previewFirst', { count: previewContacts.length })}</p>
          <div className="border border-border rounded-xl overflow-hidden max-h-60 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-background sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted">{t('calls.phone')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted">{t('calls.contactName')}</th>
                  <th className="text-left px-3 py-2 font-medium text-muted">{t('calls.context')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {previewContacts.map((c, i) => (
                  <tr key={i} className="hover:bg-hover">
                    <td className="px-3 py-2 font-mono text-xs">{c.phone}</td>
                    <td className="px-3 py-2 text-xs">{c.name || '-'}</td>
                    <td className="px-3 py-2 text-xs text-muted">
                      {c.context && Object.keys(c.context).length > 0
                        ? Object.keys(c.context).join(', ')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Dictionary — field descriptions for AI context */}
      {previewContacts.length > 0 && !isValidating && (() => {
        // Include top-level name field (extracted by validator from NOMBRE/nombre)
        // so the user can set a format/description for it
        const topLevelKeys = [];
        if (previewContacts.some(c => c.name)) topLevelKeys.push('name');
        const contextKeys = [...new Set([
          ...topLevelKeys,
          ...previewContacts.flatMap(c => c.context ? Object.keys(c.context) : []),
        ])];
        if (contextKeys.length === 0) return null;

        const hasDescriptions = Object.values(data.fieldDescriptions || {}).some(v => v && typeof v === 'string' && v.trim());

        const handleLoadTemplate = (templateId) => {
          if (!templateId) return;
          const tpl = (fieldTemplates || []).find(t => t.template_id === templateId);
          if (!tpl) return;
          // Resolve a template key to the matching current context key.
          // Exact match first, then case-insensitive + trimmed fallback so a
          // template saved from a slightly different file (casing/whitespace)
          // still populates the fields instead of silently dropping them.
          // Mirror the backend _normalize_header canonicalization so a template
          // saved from one file still resolves against a file whose headers differ
          // only by case, accents, or separator style. Backend stores context keys
          // as the raw stripped header (FEC_ULTCORTE / "FEC ULTCORTE" / Fec-UltCorte),
          // so we must treat spaces, underscores and hyphens as equivalent and drop
          // accents (NFD + strip combining marks) before comparing.
          const norm = (s) =>
            String(s || '')
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .trim()
              .toLowerCase()
              .replace(/[\s_-]+/g, '_')
              .replace(/^_+|_+$/g, '');
          const resolveKey = (tplKey) => {
            if (contextKeys.includes(tplKey)) return tplKey;
            const target = norm(tplKey);
            return contextKeys.find((ck) => norm(ck) === target) || null;
          };
          const merged = { ...data.fieldDescriptions };
          Object.entries(tpl.field_descriptions || {}).forEach(([k, v]) => {
            const ck = resolveKey(k);
            // Coerce to string — null/non-string values crash v.trim() elsewhere
            if (ck) merged[ck] = v == null ? '' : String(v);
          });
          onChange('fieldDescriptions', merged);
          // Also merge field_formats from template
          const mergedFormats = { ...data.fieldFormats };
          Object.entries(tpl.field_formats || {}).forEach(([k, v]) => {
            const ck = resolveKey(k);
            if (ck) mergedFormats[ck] = v;
          });
          onChange('fieldFormats', mergedFormats);
          const applied = Object.keys(tpl.field_descriptions || {})
            .map(resolveKey).filter(Boolean).length;
          const total = Object.keys(tpl.field_descriptions || {}).length;
          if (applied === 0 && total > 0) {
            toast.error(`Plantilla cargada pero ninguna columna coincide (${total} campos). Revisa que el archivo tenga las mismas columnas.`);
          } else {
            toast.success(t('campaignWizard.templateLoaded') + (applied < total ? ` (${applied}/${total})` : ''));
          }
        };

        const handleSaveTemplate = async () => {
          const name = templateName.trim();
          if (!name) return;
          setSavingTemplate(true);
          try {
            const res = await createFieldTemplate(name, data.fieldDescriptions, data.fieldFormats);
            setFieldTemplates(prev => [...prev, res.data]);
            setShowSaveTemplate(false);
            setTemplateName('');
            toast.success(t('campaignWizard.templateSaved'));
          } catch (err) {
            const msg = extractApiError(err, 'Failed to save template');
            toast.error(msg);
          } finally {
            setSavingTemplate(false);
          }
        };

        return (
          <div>
            <div className="mb-3">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4" />
                {t('campaignWizard.dataDictionary')}
              </h4>
              <p className="text-xs text-muted mt-1">
                {t('campaignWizard.dataDictionaryHelp')}
              </p>
            </div>

            {/* Template Load / Save row */}
            <div className="flex items-center gap-3 mb-3">
              <div className="relative flex-1" ref={templateDropdownRef}>
                <button
                  type="button"
                  onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-card"
                >
                  <span className="text-muted">{t('campaignWizard.loadTemplate')}</span>
                  <ChevronDownIcon className="w-3.5 h-3.5 text-muted" />
                </button>
                {templateDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                    {(fieldTemplates || []).length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted">
                        {t('campaignWizard.noTemplates')}
                      </div>
                    ) : (
                      (fieldTemplates || []).map((tpl) => (
                        <div
                          key={tpl.template_id}
                          className="flex items-center justify-between px-3 py-2 hover:bg-hover transition-colors group"
                        >
                          <button
                            type="button"
                            className="flex-1 text-left text-xs text-foreground truncate"
                            onClick={() => {
                              handleLoadTemplate(tpl.template_id);
                              setTemplateDropdownOpen(false);
                            }}
                          >
                            {tpl.name}
                            <span className="ml-1 text-muted">
                              ({Object.keys(tpl.field_descriptions || {}).length})
                            </span>
                          </button>
                          <button
                            type="button"
                            className="ml-2 p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('common.delete')}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(t('campaignWizard.deleteTemplateConfirm', { name: tpl.name }))) return;
                              try {
                                await deleteFieldTemplate(tpl.template_id);
                                setFieldTemplates(prev => prev.filter(x => x.template_id !== tpl.template_id));
                                toast.success(t('campaignWizard.templateDeleted'));
                              } catch (err) {
                                toast.error(extractApiError(err, 'Failed to delete template'));
                              }
                            }}
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {!showSaveTemplate ? (
                <button
                  type="button"
                  onClick={() => setShowSaveTemplate(true)}
                  disabled={!hasDescriptions}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-border hover:bg-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <BookmarkIcon className="w-3.5 h-3.5" />
                  {t('campaignWizard.saveAsTemplate')}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={t('campaignWizard.templateNamePlaceholder')}
                    maxLength={100}
                    className="px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary w-40"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTemplate();
                      if (e.key === 'Escape') { setShowSaveTemplate(false); setTemplateName(''); }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveTemplate}
                    disabled={!templateName.trim() || savingTemplate}
                    className="px-3 py-1.5 text-xs font-medium rounded bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-40"
                  >
                    {savingTemplate ? '...' : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                    className="p-1 text-muted hover:text-foreground"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2 border border-border rounded-xl p-4 max-h-64 overflow-y-auto bg-card">
              {contextKeys.map((key) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-muted w-36 flex-shrink-0 truncate" title={key}>
                    {key}
                  </span>
                  <input
                    type="text"
                    value={(data.fieldDescriptions || {})[key] || ''}
                    onChange={(e) => {
                      const updated = { ...(data.fieldDescriptions || {}), [key]: e.target.value };
                      onChange('fieldDescriptions', updated);
                    }}
                    placeholder={t('campaignWizard.dataDictionaryPlaceholder')}
                    maxLength={100}
                    className="flex-1 px-3 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-card"
                  />
                  <select
                    value={(data.fieldFormats || {})[key] || 'none'}
                    onChange={(e) => {
                      const updated = { ...(data.fieldFormats || {}), [key]: e.target.value };
                      onChange('fieldFormats', updated);
                    }}
                    className="w-32 flex-shrink-0 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-card"
                    title={t('campaignWizard.fieldFormat', 'Format')}
                  >
                    <option value="none">Auto</option>
                    <option value="name_ap1_ap2_nom1_nom2">Nombre: AP1 AP2 NOM1 NOM2</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Invalid details */}
      {invalidDetails.length > 0 && !isValidating && (
        <div>
          <p className="text-sm font-medium text-red-600 mb-2">{t('campaignWizard.invalidContacts', { count: invalidDetails.length })}</p>
          <div className="border border-red-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto bg-red-50">
            {invalidDetails.slice(0, 10).map((inv, i) => (
              <div key={i} className="px-3 py-1.5 text-xs border-b border-red-100 last:border-0">
                <span className="font-mono">{inv.phone || '(empty)'}</span>
                <span className="text-red-500 ml-2">{inv.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 3: Schedule
const StepSchedule = ({ data, onChange, t }) => (
  <div className="space-y-6 max-w-xl mx-auto">
    <div>
      <label className="block text-sm font-medium text-foreground mb-3">
        {t('campaignWizard.allowedCallingHours', 'Allowed calling hours')}
      </label>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('campaignWizard.startHour')}
          </label>
          <select
            value={data.startHour}
            onChange={(e) => onChange('startHour', Number.parseInt(e.target.value, 10))}
            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-card"
          >
            {CALLING_HOUR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('campaignWizard.endHour')}
          </label>
          <select
            value={data.endHour}
            onChange={(e) => onChange('endHour', Number.parseInt(e.target.value, 10))}
            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-card"
          >
            {CALLING_HOUR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-muted mt-2">
        {t('campaignWizard.allowedCallingHoursHelp', 'Calls will only be placed within these hours (CDMX time).')}
      </p>
    </div>
    <div>
      <label className="block text-sm font-medium text-foreground mb-3">
        {t('campaignWizard.whenToStart')}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => onChange('scheduleType', 'now')}
          className={cn(
            'p-4 rounded-lg border-2 text-left transition-colors',
            data.scheduleType === 'now'
              ? 'border-primary bg-primary-50'
              : 'border-border hover:border-border'
          )}
        >
          <BoltIcon className="w-5 h-5 text-primary mb-2" />
          <p className="font-medium text-foreground">{t('campaignWizard.startNow')}</p>
          <p className="text-xs text-muted">{t('campaignWizard.startNowDesc')}</p>
        </button>
        <button
          onClick={() => onChange('scheduleType', 'scheduled')}
          className={cn(
            'p-4 rounded-lg border-2 text-left transition-colors',
            data.scheduleType === 'scheduled'
              ? 'border-primary bg-primary-50'
              : 'border-border hover:border-border'
          )}
        >
          <CalendarIcon className="w-5 h-5 text-primary mb-2" />
          <p className="font-medium text-foreground">{t('campaignWizard.scheduleLabel')}</p>
          <p className="text-xs text-muted">{t('campaignWizard.scheduleDesc')}</p>
        </button>
      </div>
    </div>

    {data.scheduleType === 'scheduled' && (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('campaignWizard.dateLabel')}</label>
          <input
            type="date"
            value={data.scheduledDate}
            onChange={(e) => onChange('scheduledDate', e.target.value)}
            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('campaignWizard.timeLabel')}</label>
          <input
            type="time"
            value={data.scheduledTime}
            onChange={(e) => onChange('scheduledTime', e.target.value)}
            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-card"
          />
          <p className="text-xs text-muted mt-1">{t('campaignWizard.cdmxNote')}</p>
        </div>
      </div>
    )}

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.concurrency')}: <span className="text-primary">{data.concurrency}</span>
      </label>
      <input
        type="range"
        min="1"
        max="5"
        value={data.concurrency}
        onChange={(e) => onChange('concurrency', parseInt(e.target.value))}
        className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <div className="flex justify-between text-xs text-muted mt-1">
        <span>1 (slower)</span>
        <span>5 (max)</span>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {t('campaignWizard.callsPerMinute')}: <span className="text-primary">{data.callsPerMinute}</span>
      </label>
      <input
        type="range"
        min="1"
        max="20"
        value={data.callsPerMinute}
        onChange={(e) => onChange('callsPerMinute', parseInt(e.target.value))}
        className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <div className="flex justify-between text-xs text-muted mt-1">
        <span>1/min</span>
        <span>20/min</span>
      </div>
    </div>

    {/* Retry Settings */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {t('campaignWizard.maxRetriesLabel')}: <span className="text-primary">{data.maxRetries}</span>
        </label>
        <input
          type="range"
          min="0"
          max="10"
          value={data.maxRetries}
          onChange={(e) => onChange('maxRetries', parseInt(e.target.value))}
          className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted mt-1">
          <span>{t('campaignWizard.noRetries')}</span>
          <span>10</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {t('campaignWizard.retryDelayLabel')}: <span className="text-primary">{data.retryDelayMinutes}</span>
        </label>
        <input
          type="range"
          min="5"
          max="240"
          step="5"
          value={data.retryDelayMinutes}
          onChange={(e) => onChange('retryDelayMinutes', parseInt(e.target.value))}
          className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted mt-1">
          <span>5 min</span>
          <span>240 min</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {t('campaignWizard.maxRetriesPerDayLabel')}: <span className="text-primary">{data.maxRetriesPerDay}</span>
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={data.maxRetriesPerDay}
          onChange={(e) => onChange('maxRetriesPerDay', parseInt(e.target.value))}
          className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted mt-1">
          <span>1</span>
          <span>10</span>
        </div>
        <p className="text-xs text-muted mt-1">{t('campaignWizard.maxRetriesPerDayHint')}</p>
      </div>
    </div>

    <div className="p-4 bg-background rounded-xl border border-border">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <ClockIcon className="w-5 h-5 text-muted" />
          <div>
            <p className="text-sm font-medium text-foreground">
              ~{Math.ceil((data.totalContacts || 0) / (data.callsPerMinute || 5))} min
            </p>
            <p className="text-xs text-muted">{t('campaignWizard.estimatedDuration')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CurrencyDollarIcon className="w-5 h-5 text-muted" />
          <div>
            <p className="text-sm font-medium text-foreground">
              ~${((data.totalContacts || 0) * 0.02).toFixed(2)}
            </p>
            <p className="text-xs text-muted">{t('campaignWizard.estimatedCost')}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Step 4: Review
const StepReview = ({ data, agents, t }) => {
  const agent = agents.find(a => a.id === data.agentId);

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <RocketLaunchIcon className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">{t('campaignWizard.reviewLaunch')}</h2>
        <p className="text-sm text-muted">{t('campaignWizard.reviewSubtitle')}</p>
      </div>

      <div className="bg-card rounded-xl border border-border divide-y divide-border">
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.campaignName')}</span>
          <span className="text-sm font-medium text-foreground">{data.name || '-'}</span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.selectAgent')}</span>
          <span className="text-sm font-medium text-foreground">{agent?.name || '-'}</span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.callerIdLabel')}</span>
          <span className="text-sm font-medium text-foreground">
            {data.callerIds.length > 0 ? data.callerIds.join(', ') : '-'}
          </span>
        </div>
        {data.goalDescription && (
          <div className="p-4">
            <span className="text-sm text-muted block mb-1">{t('campaignWizard.campaignGoal', 'Campaign Goal')}</span>
            <span className="text-sm font-medium text-foreground">{data.goalDescription}</span>
          </div>
        )}
        {data.genderSwitch && (
          <div className="p-4 flex justify-between">
            <span className="text-sm text-muted">{t('campaignWizard.genderSwitch')}</span>
            <span className="text-sm font-medium text-green-600">{t('campaignWizard.genderSwitchEnabled')}</span>
          </div>
        )}
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaigns.contacts', 'Contacts')}</span>
          <span className="text-sm font-medium text-foreground">
            {t('campaignWizard.numberCount', { count: data.totalContacts || 0 })}
          </span>
        </div>
        {(() => {
          const configured = Object.entries(data.fieldDescriptions || {}).filter(([, v]) => v && typeof v === 'string' && v.trim());
          const configuredFormats = Object.entries(data.fieldFormats || {}).filter(([, v]) => v && v !== 'none');
          if (configured.length === 0 && configuredFormats.length === 0) return null;
          return (
            <div className="p-4">
              <span className="text-sm text-muted block mb-2">{t('campaignWizard.fieldDescriptions')}</span>
              <div className="space-y-1">
                {configured.map(([key, desc]) => {
                  const fmt = (data.fieldFormats || {})[key];
                  return (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-muted">{key}</span>
                      <span className="text-foreground">{desc}</span>
                      {fmt && fmt !== 'none' && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[10px]">
                          {fmt === 'name_ap1_ap2_nom1_nom2' ? 'Nombre' : fmt}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.source')}</span>
          <span className="text-sm font-medium text-foreground">
            {data.webhookContacts > 0 && !data.uploadedFile && !data.parsedContacts?.length
              ? t('campaignWizard.sourceWebhook')
              : data.uploadedFile
              ? t('campaignWizard.sourceFile')
              : t('campaignWizard.sourceManual')}
          </span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaigns.start', 'Start')}</span>
          <span className="text-sm font-medium text-foreground">
            {data.scheduleType === 'now' ? t('campaignWizard.immediately') : `${data.scheduledDate} ${data.scheduledTime}`}
          </span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.allowedCallingHours', 'Allowed calling hours')}</span>
          <span className="text-sm font-medium text-foreground">
            {`${String(data.startHour).padStart(2, '0')}:00 - ${String(data.endHour).padStart(2, '0')}:00`}
          </span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.concurrency')}</span>
          <span className="text-sm font-medium text-foreground">{data.concurrency} {t('campaigns.calls')}</span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.retries')}</span>
          <span className="text-sm font-medium text-foreground">{data.maxRetries} ({t('campaignWizard.eachRetry', { minutes: data.retryDelayMinutes })})</span>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-muted">{t('campaignWizard.rate')}</span>
          <span className="text-sm font-medium text-foreground">{t('campaignWizard.callsPerMinuteRate', { n: data.callsPerMinute })}</span>
        </div>
        <div className="p-4 flex justify-between bg-background">
          <span className="text-sm font-medium text-foreground">{t('campaignWizard.estimatedCost')}</span>
          <span className="text-sm font-semibold text-primary">
            ${((data.totalContacts || 0) * 0.02).toFixed(2)}
          </span>
        </div>
      </div>

      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 flex gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
        <p className="text-sm text-yellow-800">
          {t('campaignWizard.warningBanner', { when: data.scheduleType === 'now' ? t('campaignWizard.warningWhenNow') : t('campaignWizard.warningWhenScheduled') })}
        </p>
      </div>
    </div>
  );
};

function CampaignWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: editCampaignId } = useParams(); // truthy when route is /campaigns/:id/edit
  const isEditMode = Boolean(editCampaignId);
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  // Save-as-Draft is a SEPARATE, non-launching action: it creates the campaign
  // (backend already defaults status=draft) and uploads contacts, but NEVER
  // changes status to running/scheduled and never touches any schedule/timer.
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState(null);
  // In-flight guard shared by BOTH actions so a Save-Draft and a Launch (or two
  // rapid Save-Draft clicks) can never run concurrently and double-create.
  const inFlightRef = useRef(false);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [fieldTemplates, setFieldTemplates] = useState([]);
  // campaignId is set only when a draft has been created on the backend.
  // For new (non-edit) campaigns it starts null and is set either:
  //   a) When the user clicks "Fetch Contacts" (webhook path needs it), or
  //   b) At Launch time (upload/paste paths).
  const [campaignId, setCampaignId] = useState(null);
  // launchedRef stays false until a successful launch.  The unmount cleanup
  // uses this to decide whether to delete an orphan draft.
  const launchedRef = useRef(isEditMode); // edit mode: never auto-delete
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    agentId: '',
    callerIds: [],
    phoneList: '',
    validCount: 0,
    invalidCount: 0,
    duplicateCount: 0,
    sharedPhoneCount: 0,
    sharedNumberCount: 0,
    totalContacts: 0,
    uploadedFile: null,
    parsedContacts: null,
    webhookUrl: '',
    webhookContacts: 0,
    datafyJson: '',
    scheduleType: 'now',
    scheduledDate: '',
    scheduledTime: '',
    concurrency: 3,
    callsPerMinute: 5,
    goalDescription: '',
    fieldDescriptions: {},
    fieldFormats: {},
    genderSwitch: false,
    maxRetries: 3,
    // Retry-delay default 120min (2h) matches Roger's approved policy + backend
    // default (PR #1174). Edit mode overrides from c.retry_delay_minutes below.
    retryDelayMinutes: 120,
    maxRetriesPerDay: 3,
    assignedCallers: [],
    // New-campaign defaults: business-hours window 9–18 (America/Mexico_City),
    // matching backend new-campaign defaults (PR #1174). Edit mode overrides
    // these from the campaign's stored start_hour/end_hour below.
    startHour: 9,
    endHour: 18,
    visibility: 'individual',
  });

  // Fetch agents, phone numbers, templates — and existing campaign in edit mode.
  useEffect(() => {
    const fetchData = async () => {
      try {
      // Use allSettled so a single failing endpoint (e.g. phone-numbers 500)
      // does not block the whole wizard — partial data is better than blank.
      const requests = [
        api.get('/agents'),
        listPhoneNumbers(),
        listFieldTemplates(),
        ...(isEditMode ? [getCampaign(editCampaignId)] : []),
      ];
      const [agentsResult, phoneResult, templatesResult, campaignResult] =
        await Promise.allSettled(requests);

      if (agentsResult.status === 'fulfilled') {
        setAgents((agentsResult.value?.data || []).filter(Boolean));
      } else {
        console.error('[CampaignWizard] Failed to load agents:', agentsResult.reason);
        toast.error(t('errors.failedFetchAgents'));
      }

      if (phoneResult.status === 'fulfilled') {
        setPhoneNumbers((phoneResult.value?.data || []).filter(Boolean));
      } else {
        console.error('[CampaignWizard] Failed to load phone numbers:', phoneResult.reason);
        // Non-fatal: wizard still usable, phone selector will be empty
      }

      if (templatesResult.status === 'fulfilled') {
        setFieldTemplates((templatesResult.value?.data || []).filter(Boolean));
      } else {
        console.error('[CampaignWizard] Failed to load field templates:', templatesResult.reason);
        // Non-fatal: template selector will be empty
      }

      // Edit mode: prefill form from existing campaign data
      if (isEditMode && campaignResult) {
        if (campaignResult.status === 'fulfilled') {
          const c = campaignResult.value?.data || {};
          setCampaignId(c.campaign_id);
          setFormData(prev => ({
            ...prev,
            name: c.name || '',
            description: c.description || '',
            agentId: c.agent_id || '',
            callerIds: c.caller_ids || [],
            scheduleType: c.schedule_type || 'now',
            scheduledDate: c.scheduled_start ? c.scheduled_start.split('T')[0] : '',
            scheduledTime: c.scheduled_start ? c.scheduled_start.split('T')[1]?.slice(0, 5) : '',
            concurrency: c.concurrency ?? prev.concurrency,
            callsPerMinute: c.calls_per_minute ?? prev.callsPerMinute,
            maxRetries: c.max_retries ?? prev.maxRetries,
            retryDelayMinutes: c.retry_delay_minutes ?? prev.retryDelayMinutes,
            maxRetriesPerDay: c.max_retries_per_day ?? prev.maxRetriesPerDay,
            goalDescription: c.goal_description || '',
            genderSwitch: c.gender_switch || false,
            visibility: c.visibility || 'individual',
            startHour: Number.isInteger(c.start_hour) ? c.start_hour : prev.startHour,
            endHour: Number.isInteger(c.end_hour) ? c.end_hour : prev.endHour,
            totalContacts: c.total_contacts || 0,
          }));
        } else {
          console.error('[CampaignWizard] Failed to load campaign for edit:', campaignResult.reason);
          toast.error(t('errors.failedLoadCampaign'));
        }
      }
      } catch (err) {
        console.error('Failed to load wizard data:', err);
        toast.error(t('errors.failedFetchAgents'));
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup: if user abandons a newly-created draft (cancel/navigate away without launching),
  // delete it so it doesn't appear as an orphan in the list.
  useEffect(() => {
    return () => {
      if (campaignId && !launchedRef.current) {
        deleteCampaign(campaignId).catch(() => {});
      }
    };
  }, [campaignId]); // re-runs only when campaignId changes (set once)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Lazily creates a campaign draft and returns its id.
   * Called by the webhook "Fetch Contacts" path which requires a campaign_id.
   * Safe to call multiple times — returns existing id if already created.
   */
  const ensureCampaignId = useCallback(async () => {
    if (campaignId) return campaignId;
    try {
      const res = await createCampaign({
        name: formData.name,
        description: formData.description,
        agent_id: formData.agentId,
        caller_ids: formData.callerIds,
        schedule_type: formData.scheduleType,
        concurrency: formData.concurrency,
        calls_per_minute: formData.callsPerMinute,
        max_retries: formData.maxRetries,
        retry_delay_minutes: formData.retryDelayMinutes,
        max_retries_per_day: formData.maxRetriesPerDay,
        goal_description: formData.goalDescription,
        gender_switch: formData.genderSwitch,
        visibility: formData.visibility || 'individual',
        start_hour: Number.isInteger(formData.startHour) ? formData.startHour : 9,
        end_hour: Number.isInteger(formData.endHour) ? formData.endHour : 18,
      });
      const newId = res.data.campaign_id;
      setCampaignId(newId);
      return newId;
    } catch (err) {
      toast.error(extractApiError(err, t('errors.failedLoadCampaign')));
      return null;
    }
  }, [campaignId, formData, t]);

  /**
   * Persist a NEW campaign's non-status content: schedule/config settings, field
   * descriptions, and contacts. This is the shared body used by BOTH "Launch"
   * and "Save as Draft" for new (non-edit) campaigns. It NEVER changes campaign
   * status and NEVER starts a schedule/timer — the caller decides whether to flip
   * status afterwards (Launch does; Save Draft does not).
   *
   * For Save Draft we still persist the chosen schedule_type/scheduled_start as
   * plain settings so a later Launch preserves the user's intent, but because the
   * status stays "draft" the dialer/scheduler treat the campaign as inert.
   *
   * Returns { ok: true } on success, or { ok: false, error, noContacts } so the
   * caller can surface a visible error without changing status.
   */
  const persistNewCampaignContent = async (activeCampaignId) => {
    // Save schedule + tuning settings (as data only; status is untouched here).
    await updateCampaign(activeCampaignId, {
      schedule_type: formData.scheduleType,
      scheduled_start: formData.scheduleType === 'scheduled'
        ? `${formData.scheduledDate}T${formData.scheduledTime}:00Z`
        : null,
      concurrency: formData.concurrency,
      calls_per_minute: formData.callsPerMinute,
      max_retries: formData.maxRetries,
      retry_delay_minutes: formData.retryDelayMinutes,
      max_retries_per_day: formData.maxRetriesPerDay,
      gender_switch: formData.genderSwitch,
      start_hour: Number.isInteger(formData.startHour) ? formData.startHour : 9,
      end_hour: Number.isInteger(formData.endHour) ? formData.endHour : 18,
    });

    // Save field descriptions if any
    const nonEmpty = Object.fromEntries(
      Object.entries(formData.fieldDescriptions || {}).filter(([, v]) => typeof v === 'string' && v.trim())
    );
    const nonDefaultFormats = Object.fromEntries(
      Object.entries(formData.fieldFormats || {}).filter(([, v]) => v && v !== 'none')
    );
    if (Object.keys(nonEmpty).length > 0 || Object.keys(nonDefaultFormats).length > 0) {
      try {
        await updateFieldDescriptions(activeCampaignId, nonEmpty, nonDefaultFormats);
      } catch (err) {
        console.error('Failed to save field descriptions:', err);
      }
    }

    // Upload contacts (idempotent per backend reupload-dedup). No status change here.
    if (formData.webhookContacts > 0 && !formData.uploadedFile && !formData.parsedContacts?.length) {
      // Webhook contacts — already stored, nothing to upload
    } else if (formData.uploadedFile) {
      await uploadContactsFile(activeCampaignId, formData.uploadedFile);
    } else if (formData.parsedContacts?.length > 0) {
      await uploadContactsJson(activeCampaignId, formData.parsedContacts);
    } else {
      return { ok: false, noContacts: true };
    }
    return { ok: true };
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.agentId && formData.callerIds.length > 0;
      case 2:
        return formData.totalContacts > 0;
      case 3:
        return formData.scheduleType === 'now' || (formData.scheduledDate && formData.scheduledTime);
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    // NOTE: campaign creation is deferred to Launch (or to "Fetch Contacts" for the
    // webhook path).  We no longer create a draft on Step 1 → 2 navigation.

    if (currentStep < 4) {
      // Field descriptions and schedule settings are now persisted at Launch time,
      // so no intermediate saves are needed here for new campaigns.
      // For edit mode, settings are also persisted at Launch.
      setCurrentStep(prev => prev + 1);
    } else {
      setShowConfirm(true);
    }
  };

  const handleLaunch = async () => {
    // Duplicate-precheck: never let two submits (Launch/Save-Draft) overlap.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLaunching(true);
    setLaunchError(null);
    try {
      let activeCampaignId = campaignId;

      if (isEditMode) {
        // Edit mode: update existing campaign settings then re-launch
        await updateCampaign(activeCampaignId, {
          name: formData.name,
          description: formData.description,
          agent_id: formData.agentId,
          caller_ids: formData.callerIds,
          schedule_type: formData.scheduleType,
          scheduled_start: formData.scheduleType === 'scheduled'
            ? `${formData.scheduledDate}T${formData.scheduledTime}:00Z`
            : null,
          concurrency: formData.concurrency,
          calls_per_minute: formData.callsPerMinute,
          max_retries: formData.maxRetries,
          retry_delay_minutes: formData.retryDelayMinutes,
          max_retries_per_day: formData.maxRetriesPerDay,
          goal_description: formData.goalDescription,
          gender_switch: formData.genderSwitch,
          visibility: formData.visibility || 'individual',
          start_hour: Number.isInteger(formData.startHour) ? formData.startHour : 9,
          end_hour: Number.isInteger(formData.endHour) ? formData.endHour : 18,
        });
      } else {
        // New campaign: create draft now (if not already created by webhook path)
        if (!activeCampaignId) {
          const newId = await ensureCampaignId();
          if (!newId) return; // creation failed, error already toasted
          activeCampaignId = newId;
        }

        // Pre-flight: skip mutation if campaign is already running/completed
        const { data: campaignData } = await getCampaign(activeCampaignId);
        if (!['draft', 'scheduled'].includes(campaignData.status)) {
          // Campaign already started — just navigate away without false-success toast
          launchedRef.current = true;
          setShowConfirm(false);
          navigate(`/campaigns/${activeCampaignId}`);
          return;
        }

        // Persist settings + field descriptions + contacts (shared with Save Draft).
        const prep = await persistNewCampaignContent(activeCampaignId);
        if (!prep.ok) {
          // No contacts source — abort with visible error before status change
          setLaunchError(t('errors.noContactsToLaunch'));
          return;
        }
      }

      // Update status based on schedule type
      const targetStatus = formData.scheduleType === 'scheduled' ? 'scheduled' : 'running';
      try {
        await updateCampaignStatus(activeCampaignId, targetStatus);
        launchedRef.current = true; // prevent orphan cleanup on unmount
        toast.success(t('success.campaignStarted'));
        navigate(`/campaigns/${activeCampaignId}`);
      } catch (statusErr) {
        // Guard: 403 means the caller/manager lacks status-change permission.
        if (statusErr?.response?.status === 403) {
          console.error('[CampaignWizard] 403 on status update — insufficient role', statusErr);
          toast.error(t('errors.campaignStartForbidden'));
          return;
        }
        throw statusErr;
      }
    } catch (err) {
      console.error('[CampaignWizard] handleLaunch error', err);
      setLaunchError(extractApiError(err, t('errors.failedStartCampaign')));
    } finally {
      setIsLaunching(false);
      inFlightRef.current = false;
    }
  };

  /**
   * Save-as-Draft: create the campaign (backend defaults status=draft) and upload
   * its contacts, then STOP. This is a genuinely distinct, non-launching action:
   *   - It NEVER calls updateCampaignStatus (no draft→running / →scheduled flip).
   *   - It NEVER starts a schedule/timer; scheduled_start is stored as plain data
   *     only, and because status stays "draft" the dialer/scheduler ignore it.
   *   - It reuses ONLY the existing create + upload endpoints (no new backend,
   *     no PATCH of a running campaign, no credential/JWT tricks).
   * Agent/caller/visibility/context are retained via the same create payload used
   * by Launch. Edit mode is excluded — an existing campaign is saved via its own
   * update flow, not "created" as a draft.
   */
  const handleSaveDraft = async () => {
    if (isEditMode) return; // guarded by UI too; draft-create is new-campaign only
    // Duplicate-precheck: never overlap with Launch or a second Save-Draft click.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSavingDraft(true);
    setDraftError(null);
    try {
      // Reuse the same create path Launch uses; returns existing id if already
      // created (webhook path or a prior Save-Draft) — no duplicate campaign.
      let activeCampaignId = campaignId;
      if (!activeCampaignId) {
        const newId = await ensureCampaignId();
        if (!newId) return; // creation failed, error already toasted
        activeCampaignId = newId;
      }

      // Idempotence guard: if a prior save already advanced this campaign past
      // draft (e.g. it was launched in another tab), do NOT re-mutate it.
      const { data: campaignData } = await getCampaign(activeCampaignId);
      if (!['draft'].includes(campaignData.status)) {
        launchedRef.current = true; // keep it; it's a real, non-orphan campaign
        setShowDraftConfirm(false);
        navigate(`/campaigns/${activeCampaignId}`);
        return;
      }

      const prep = await persistNewCampaignContent(activeCampaignId);
      if (!prep.ok) {
        setDraftError(t('errors.noContactsToSaveDraft'));
        return;
      }

      // Intentional draft save: mark as non-orphan so the unmount cleanup does
      // NOT delete it. Crucially, we do NOT change status — it stays "draft".
      launchedRef.current = true;
      setShowDraftConfirm(false);
      toast.success(t('success.campaignDraftSaved'));
      navigate(`/campaigns/${activeCampaignId}`);
    } catch (err) {
      console.error('[CampaignWizard] handleSaveDraft error', err);
      setDraftError(extractApiError(err, t('errors.failedSaveDraft')));
    } finally {
      setIsSavingDraft(false);
      inFlightRef.current = false;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center gap-2 text-sm text-muted hover:text-foreground mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('common.back')} {t('nav.campaigns')}
        </button>
        <h1 className="text-2xl font-semibold text-foreground">
          {isEditMode ? t('campaignWizard.editCampaign', 'Edit Campaign') : t('campaignWizard.createCampaign')}
        </h1>
      </div>

      {/* Stepper */}
      <Stepper currentStep={currentStep} t={t} />

      {/* Step content */}
      <div className="bg-card rounded-xl border border-border p-8 mb-6">
        {currentStep === 1 && (
          <StepBasics
            data={formData}
            onChange={handleChange}
            agents={agents}
            phoneNumbers={phoneNumbers}
            t={t}
          />
        )}
        {currentStep === 2 && (
          <StepAudience
            data={formData}
            onChange={handleChange}
            campaignId={campaignId}
            onEnsureCampaignId={ensureCampaignId}
            fieldTemplates={fieldTemplates}
            setFieldTemplates={setFieldTemplates}
          />
        )}
        {currentStep === 3 && <StepSchedule data={formData} onChange={handleChange} t={t} />}
        {currentStep === 4 && <StepReview data={formData} agents={agents} t={t} />}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            if (currentStep === 1) {
              navigate('/campaigns');
            } else {
              setCurrentStep(prev => prev - 1);
            }
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted hover:text-foreground hover:bg-hover"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {currentStep === 1 ? t('common.cancel') : t('campaignWizard.previous')}
        </button>
        <div className="flex items-center gap-3">
          {/* Save as Draft — distinct, non-launching action. Only for NEW campaigns
              on the final step. Disabled while any action is in flight or contacts
              are missing, so it can never double-create or save an empty draft. */}
          {currentStep === 4 && !isEditMode && (
            <button
              type="button"
              onClick={() => { setDraftError(null); setShowDraftConfirm(true); }}
              disabled={!canProceed() || isLaunching || isSavingDraft}
              data-testid="save-draft-button"
              className={cn(
                'flex items-center gap-2 px-6 py-2 rounded-full text-sm font-medium transition-colors border',
                canProceed() && !isLaunching && !isSavingDraft
                  ? 'border-border text-foreground hover:bg-hover'
                  : 'border-border text-muted cursor-not-allowed'
              )}
            >
              <DocumentTextIcon className="w-4 h-4" />
              {isSavingDraft ? t('common.loading') : t('campaignWizard.saveAsDraft')}
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canProceed() || isLaunching || isSavingDraft}
            className={cn(
              'flex items-center gap-2 px-6 py-2 rounded-full text-sm font-medium transition-colors',
              canProceed() && !isLaunching && !isSavingDraft
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-secondary text-muted cursor-not-allowed'
            )}
          >
            {currentStep === 4 ? (
              <>
                <RocketLaunchIcon className="w-4 h-4" />
                {isLaunching ? t('common.loading') : t('campaignWizard.launchCampaign')}
              </>
            ) : (
              <>
                {t('common.next')}
                <ArrowRightIcon className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground mb-2">{t('campaignWizard.launchCampaign')}?</h3>
            {launchError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {launchError}
              </div>
            )}
            <p className="text-sm text-muted mb-6">
              {formData.webhookContacts > 0 && !formData.uploadedFile && !formData.parsedContacts?.length
                ? t('campaignWizard.confirmUseWebhook', { count: formData.totalContacts.toLocaleString() })
                : t('campaignWizard.confirmUpload', { count: formData.totalContacts.toLocaleString() })}
              {' '}{formData.scheduleType === 'now' ? t('campaignWizard.confirmAndStartNow') : t('campaignWizard.confirmAndSchedule', { date: formData.scheduledDate })}.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirm(false); setLaunchError(null); }}
                disabled={isLaunching}
                className="flex-1 px-4 py-2 border border-border rounded-full text-sm font-medium text-foreground hover:bg-hover transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleLaunch}
                disabled={isLaunching}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary-hover transition-colors"
              >
                {isLaunching ? t('common.loading') : t('campaignWizard.launchCampaign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save-as-Draft confirm modal — separate from Launch, with its own copy that
          makes clear NO calls will be placed. */}
      {showDraftConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground mb-2">{t('campaignWizard.saveDraftTitle')}</h3>
            {draftError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="save-draft-error">
                {draftError}
              </div>
            )}
            <p className="text-sm text-muted mb-6">
              {t('campaignWizard.saveDraftDesc', { count: formData.totalContacts.toLocaleString() })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDraftConfirm(false); setDraftError(null); }}
                disabled={isSavingDraft}
                className="flex-1 px-4 py-2 border border-border rounded-full text-sm font-medium text-foreground hover:bg-hover transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={isSavingDraft}
                data-testid="save-draft-confirm"
                className="flex-1 px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary-hover transition-colors"
              >
                {isSavingDraft ? t('common.loading') : t('campaignWizard.saveDraftConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignWizard;
// Named export for focused unit testing of the audience step (badge counts,
// field-template load/save round-trip). Not used by app code.
export { StepAudience };
